// mui
import { Grid, Typography, Button, Stack} from '@mui/material';
// form
import { useFieldArray, useForm } from 'react-hook-form';
// little-state-machine
import { useStateMachine } from 'little-state-machine';
import { defaultClassCodeObject } from '../../../../store/form/state/coverageAndClasses';
// component
import {
  FormProvider,
  RHFDatePicker,
  RHFSelect,
  FieldCondition
} from '../../../../components/hook-form';
import FormSectionControls from '../../FormSectionControls';
import Iconify from '../../../../components/iconify/Iconify';
// hooks
import useFormStateUpdateOnChange from '../../../../hooks/useFormStateUpdateOnChange';
import ClassCodeFields from './fields/ClassCodeFields';

// -------------------------------------------------------

const Form = () => {
  const maxClassCodes = 14;

  // state
  const { state } = useStateMachine();

  // form
  const methods = useForm({
    mode: 'onChange',
    defaultValues: state.form,
  });

  const { watch, control, setValue } = methods;

  useFormStateUpdateOnChange(watch);

  const {
    fields: classCodes,
    append: appendClassCode,
    remove: removeClassCode,
  } = useFieldArray({
    control,
    name: 'coverageAndClasses.classCodes',
    defaultValues: state.form.coverageAndClasses.classCodes,
  });

  const handleAppendClassCode = () => appendClassCode(defaultClassCodeObject);
  const handleRemoveClassCode = (index) =>
    classCodes?.length > 1 ? removeClassCode(index) : null;

  const handleEffDateChange = (date) => {
    if(date !== 'Invalid Date' && !methods.getFieldState('coverageAndClasses.desiredExpDate').value) {      
      methods.setValue('coverageAndClasses.desiredEffDate', date);
      const newDate = new Date(date);
      newDate.setFullYear(newDate.getFullYear() + 1);
      methods.setValue('coverageAndClasses.desiredExpDate', newDate);
    }
  };

  const handleClassCodeAnnualPayrollChange = (value, index) => {
    setValue(`coverageAndClasses.classCodes[${index}].annualPayroll`, value);
  }

  return (
    <FormProvider methods={methods}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <RHFDatePicker 
            clearable 
            name={'coverageAndClasses.desiredEffDate'} 
            label={'Desired Effective date'}
            onChange={handleEffDateChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <RHFDatePicker clearable name={'coverageAndClasses.desiredExpDate'} label={'Desired Expiration Date'} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant={'subtitle1'}>Employers Liability</Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid item container xs={12} sm={3}>
            <RHFSelect
              name={'coverageAndClasses.employersLiability.eachAccident'}
              label={'Each Accident'}
              options={['$100,000', '$500,000', '$1,000,000']}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item container xs={12} sm={3}>
            <RHFSelect
              name={'coverageAndClasses.employersLiability.diseasePolicyLimit'}
              label={'Disease - Policy Limit'}
              options={['$500,000', '$1,000,000']}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item container xs={12} sm={3}>
            <RHFSelect
              name={'coverageAndClasses.employersLiability.diseaseEachEmployee'}
              label={'Disease - Each Employee'}
              options={['$100,000', '$500,000', '$1,000,000']}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
            <Stack spacing={2}>
              {classCodes?.map(({ id, annualPayroll }, idx) => (
                <ClassCodeFields
                  onRemove={handleRemoveClassCode}
                  isRemoveDisabled={classCodes?.length === 1}
                  index={idx}
                  key={id}
                  setAnnualPayrollInState={handleClassCodeAnnualPayrollChange}
                  initialAnnualPayroll={annualPayroll}
                />
              ))}
            </Stack>
          </Grid>
          <FieldCondition fieldName="coverageAndClasses.classCodes" condition={(val) => val.length < maxClassCodes}>
            <Grid item xs={12}>
              <Stack alignItems={'flex-end'} direction={'column'}>
                <Button
                  variant={'text'}
                  onClick={handleAppendClassCode}
                  endIcon={<Iconify icon="eva:plus-fill" width={25} height={25} />}
                >
                  Add Class Code
                </Button>
              </Stack>
            </Grid>
          </FieldCondition>
          <FieldCondition fieldName="coverageAndClasses.classCodes" condition={(val) => val.length >= maxClassCodes}>
            <Grid item xs={12}>
              <Stack alignItems={'center'}>
                <Typography fontSize={15}>
                  If more than {maxClassCodes} class codes, please attach a list of all class codes.
                </Typography>
              </Stack>
            </Grid>
          </FieldCondition>
      </Grid>

      <FormSectionControls step={'coverage-and-classes'} />
    </FormProvider>
  );
};

export default Form;
